import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Flame, AlertTriangle, Clock, Leaf,
  CheckCircle2, PauseCircle, Eye, AlertCircle, Trash2, Loader2, Camera, X,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const PRIORITIES = [
  { id: "critical", label: "KRİTİK / ACİL", short: "KRİTİK", icon: Flame,     badgeCls: "bg-red-600 text-white",     dotCls: "bg-red-500" },
  { id: "high",     label: "YÜKSEK",         short: "YÜKSEK", icon: AlertTriangle, badgeCls: "bg-orange-500 text-white", dotCls: "bg-orange-500" },
  { id: "medium",   label: "ORTA",           short: "ORTA",   icon: Clock,    badgeCls: "bg-amber-400 text-slate-900", dotCls: "bg-amber-400" },
  { id: "low",      label: "NORMAL / DÜŞÜK", short: "NORMAL", icon: Leaf,     badgeCls: "bg-emerald-500 text-white", dotCls: "bg-emerald-500" },
];
const P_META = Object.fromEntries(PRIORITIES.map((p) => [p.id, p]));
const STATUSES = [
  { id: "open",       label: "Açık / Acil",  icon: AlertCircle,  chip: "bg-red-100 text-red-800 border-red-300" },
  { id: "pending",    label: "Beklemede",    icon: PauseCircle,  chip: "bg-amber-100 text-amber-800 border-amber-300" },
  { id: "reviewing",  label: "İnceleniyor",  icon: Eye,          chip: "bg-blue-100 text-blue-800 border-blue-300" },
  { id: "resolved",   label: "Çözüldü",      icon: CheckCircle2, chip: "bg-emerald-100 text-emerald-800 border-emerald-300" },
];
const S_META = Object.fromEntries(STATUSES.map((s) => [s.id, s]));

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };

export default function Notlar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ urgent_open: 0, open_total: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/op-notes", { params: { limit: 200 } }),
        api.get("/op-notes/summary"),
      ]);
      setItems(list.items || []);
      setSummary(sum);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); return () => clearInterval(t); }, [refresh]);

  const filtered = useMemo(() => {
    if (filter === "urgent")  return items.filter((n) => (n.priority === "critical" || n.priority === "high") && n.status !== "resolved");
    if (filter === "pending") return items.filter((n) => n.status !== "resolved");
    if (filter === "mine")    return items.filter((n) => n.created_by_id === user?.id);
    return items;
  }, [items, filter, user]);

  const markBusy = (id, on) => setBusyIds((prev) => { const s = new Set(prev); on ? s.add(id) : s.delete(id); return s; });

  const changeStatus = async (id, status) => {
    if (!isAdmin) { toast.error("Durum değişikliği için yönetici yetkisi gerekli"); return; }
    markBusy(id, true);
    try { await api.patch(`/op-notes/${id}`, { status }); toast.success(`Durum: ${S_META[status]?.label}`); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi"); }
    finally { markBusy(id, false); }
  };
  const remove = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Bu notu silmek istediğinize emin misiniz?")) return;
    markBusy(id, true);
    try { await api.delete(`/op-notes/${id}`); toast.success("Silindi"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi"); }
    finally { markBusy(id, false); }
  };
  const openPhoto = async (nid) => {
    try {
      const res = await api.get(`/op-notes/${nid}/photo`, { responseType: "blob" });
      setPhotoPreviewUrl(URL.createObjectURL(res.data));
    } catch { toast.error("Fotoğraf yüklenemedi"); }
  };
  const closePhoto = () => { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); setPhotoPreviewUrl(null); };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="notlar-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-blue-500/20 flex items-center justify-center">
          <ClipboardList className="w-5 h-5 text-blue-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--y-muted)]">Saha Operasyon</div>
          <div className="font-heading font-bold text-2xl text-[color:var(--y-text)]">Notlar & İhtiyaçlar</div>
        </div>
        {summary?.urgent_open > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600 text-white text-[11px] font-bold animate-pulse" data-testid="notlar-urgent-badge">
            <Flame className="w-3 h-3" /> {summary.urgent_open} acil
          </span>
        )}
        <Button onClick={() => setShowForm((v) => !v)} data-testid="notlar-toggle-form">
          <Plus className="w-4 h-4 mr-1.5" /> {showForm ? "İptal" : "Yeni Not"}
        </Button>
      </div>

      {showForm && (
        <NewNoteForm onDone={() => { setShowForm(false); refresh(); }} />
      )}

      <div className="flex gap-2 border-b border-[color:var(--y-border)] flex-wrap">
        {["all","urgent","pending","mine"].map((f) => (
          <button key={f} data-testid={`notlar-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${filter===f ? "border-[color:var(--y-accent)] text-[color:var(--y-accent)]" : "border-transparent text-[color:var(--y-muted)]"}`}>
            {f==="all"?"Tümü":f==="urgent"?"🚨 Sadece Aciller":f==="pending"?"Bekleyenler":"Kendi Notlarım"}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {loading && <div className="text-center py-8 text-[color:var(--y-muted)]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Yükleniyor...</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-14 text-[color:var(--y-muted)] text-sm">Kayıt yok.</div>
        )}
        {filtered.map((n) => {
          const pm = P_META[n.priority] || P_META.medium;
          const sm = S_META[n.status] || S_META.open;
          const isOwn = n.created_by_id === user?.id;
          const Icon = pm.icon;
          return (
            <Card key={n.id} className={`p-3 border-l-4 ${n.priority==="critical"?"border-red-500":n.priority==="high"?"border-orange-500":n.priority==="medium"?"border-amber-400":"border-emerald-500"}`} data-testid={`notlar-item-${n.id}`}>
              <div className="flex items-start gap-3">
                <Icon className={`w-4 h-4 mt-1 ${pm.dotCls.replace("bg-","text-")}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[color:var(--y-text)] whitespace-pre-wrap">{n.content}</div>
                  <div className="text-[11px] text-[color:var(--y-muted)] mt-1 flex flex-wrap gap-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded ${pm.badgeCls}`}>{pm.short}</span>
                    <span className={`inline-block px-1.5 py-0.5 border rounded ${sm.chip}`}>{sm.label}</span>
                    <span>{n.created_by_name}{isOwn && " (siz)"}</span>
                    <span>{fmtDate(n.created_at)}</span>
                    {n.photo_path && (
                      <button onClick={() => openPhoto(n.id)} className="text-blue-400 hover:underline flex items-center gap-1" data-testid={`notlar-photo-${n.id}`}>
                        <Camera className="w-3 h-3" /> Fotoğraf
                      </button>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1">
                    {STATUSES.filter((s) => s.id !== n.status).map((s) => (
                      <Button key={s.id} variant="outline" size="sm" onClick={() => changeStatus(n.id, s.id)} disabled={busyIds.has(n.id)} data-testid={`notlar-set-${s.id}-${n.id}`}>
                        <s.icon className="w-3.5 h-3.5" />
                      </Button>
                    ))}
                    <Button variant="ghost" size="sm" onClick={() => remove(n.id)} disabled={busyIds.has(n.id)} data-testid={`notlar-delete-${n.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!photoPreviewUrl} onOpenChange={(o) => { if (!o) closePhoto(); }}>
        <DialogContent className="max-w-3xl p-2">
          <div className="flex items-center justify-between px-2 py-1 border-b border-slate-100 mb-2">
            <div className="text-sm font-medium flex items-center gap-1"><Camera className="w-4 h-4" /> Fotoğraf</div>
            <button onClick={closePhoto}><X className="w-4 h-4" /></button>
          </div>
          {photoPreviewUrl && <img src={photoPreviewUrl} alt="Not fotoğrafı" className="w-full max-h-[70vh] object-contain rounded" data-testid="notlar-photo-preview" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewNoteForm({ onDone }) {
  const [content, setContent] = useState("");
  const [priority, setPriority] = useState("medium");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Dosya çok büyük (max 15MB)"); return; }
    setPhoto(f); setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    const c = (content || "").trim();
    if (!c) { toast.error("Not içeriği boş olamaz"); return; }
    const fd = new FormData();
    fd.append("content", c);
    fd.append("priority", priority);
    if (photo) fd.append("photo", photo);
    setSubmitting(true);
    try {
      await api.post("/op-notes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Not eklendi");
      setContent(""); setPriority("medium"); setPhoto(null); setPreview(null);
      onDone && onDone();
    } catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 border border-[color:var(--y-border)] rounded-lg p-4 bg-[color:var(--y-surface)]" data-testid="notlar-new-form">
      <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} maxLength={1200} placeholder="Örneğin: 15A hatta yağ eksikliği, yarın sabah kontrol edilmeli..." data-testid="notlar-new-content" />
      <div className="flex gap-2 flex-wrap">
        {PRIORITIES.map((p) => (
          <button key={p.id} type="button" onClick={() => setPriority(p.id)} data-testid={`notlar-new-prio-${p.id}`}
            className={`px-3 py-1.5 text-xs font-medium border rounded ${priority===p.id?p.badgeCls+" border-transparent":"border-[color:var(--y-border)] text-[color:var(--y-muted)]"}`}>
            <p.icon className="w-3.5 h-3.5 inline mr-1" />{p.short}
          </button>
        ))}
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-widest text-[color:var(--y-muted)] flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" /> Fotoğraf (opsiyonel)
        </label>
        <input type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])} data-testid="notlar-new-photo"
          className="text-sm mt-1 text-[color:var(--y-text-2)]" />
        {preview && <img src={preview} alt="önizleme" className="mt-2 max-h-40 rounded border border-[color:var(--y-border)]" />}
      </div>
      <Button type="submit" disabled={submitting} data-testid="notlar-new-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Kaydet
      </Button>
    </form>
  );
}
